import { inject } from '@angular/core';
import {
  type CanActivateChildFn,
  type CanDeactivateFn,
  type CanMatchFn,
  Router,
} from '@angular/router';
import { TranslocoService, type Translation } from '@jsverse/transloco';

import { loadFrenchDeferred } from '../../shared/i18n/french-catalogue';
import { SOURCE_LANGUAGE } from '../../shared/i18n/language-registry';

import { Guardianship } from './guardianship';
import type { LeavableScreen } from './journey-leaving';
import { JourneyNavigation } from './journey-navigation';
import { JourneyStart } from './journey-start';
import { JourneyStore } from './journey.store';
import type { JourneyView, PrivateCollectionId, SpiritualSectionId } from './journey.types';
import { PRIVATE_COLLECTIONS, PrivateCollections } from './private-collections';
import { SPIRITUAL_SECTION_IDS } from './spiritual-balance';

/** Every screen past the first needs a draft to show: without one, the journey starts over. */
/**
 * Every screen past the first reads the second part of the French catalogue — the fallback of
 * every language too — so it is in hand before any of them opens: fetched once, merged into
 * Transloco's French, and the first screen never waits for it.
 */
export const frenchCompleteGuard: CanActivateChildFn = async (route) => {
  if (route.data['view'] === 'home') return true;
  const transloco = inject(TranslocoService);
  try {
    const deferred = await loadFrenchDeferred();
    if (!mergedInto.has(transloco)) {
      transloco.setTranslation(deferred as Translation, SOURCE_LANGUAGE, { merge: true });
      mergedInto.add(transloco);
    }
  } catch {
    // Offline before the chunk ever arrived: the screen opens; a missing string shows its key
    // rather than the app refusing to move.
  }
  return true;
};

/** The Transloco instances the second part has already been merged into. */
const mergedInto = new WeakSet<TranslocoService>();

export const draftGuard: CanMatchFn = async () => {
  const store = inject(JourneyStore);
  const router = inject(Router);
  await inject(JourneyStart).start();
  return store.draft() !== null || router.parseUrl('/');
};

/**
 * The door in front of everything behind the phrase. An address is only a request: if the space
 * is closed — after a reload it always is — the reader is asked for the phrase, or to create one,
 * and taken to what they asked for once it is open. If it is open, what the screen reads is made
 * ready first. An id that names no private part is not a screen.
 */
export const privateScreenGuard: CanMatchFn = async (route, segments) => {
  const store = inject(JourneyStore);
  const router = inject(Router);
  const navigation = inject(JourneyNavigation);
  const collections = inject(PrivateCollections);
  const guardianship = inject(Guardianship);
  await inject(JourneyStart).start();
  if (store.draft() === null) return router.parseUrl('/');

  const view = route.data?.['view'] as JourneyView;
  const id = segments[2]?.path ?? '';
  if (view === 'private-collection' || view === 'private-item') {
    if (PRIVATE_COLLECTIONS[id as PrivateCollectionId] === undefined) return router.parseUrl('/');
    store.activeCollectionId.set(id as PrivateCollectionId);
    store.activePrivateSectionId.set(id as PrivateCollectionId);
  } else if (view === 'spiritual-balance') {
    if (!SPIRITUAL_SECTION_IDS.includes(id as SpiritualSectionId)) return router.parseUrl('/');
    store.activePrivateSectionId.set(id as SpiritualSectionId);
  } else {
    store.activePrivateSectionId.set(view === 'estate' ? 'estate' : 'guardianship');
  }

  const session = store.session;
  if (!session.isUnlocked) {
    if (store.draft()?.encryptedPrivateVault === null) {
      store.creationOrigin.set('private-section');
      return router.parseUrl('/phrase/creer');
    }
    return router.parseUrl('/deverrouiller');
  }

  try {
    await navigation.preparePrivateTarget();
  } catch {
    return false;
  }
  const vault = session.requireVault();
  if (view === 'private-collection' || view === 'private-item') collections.load(vault);
  if (view === 'guardianship') guardianship.load(vault);
  return true;
};

/** A screen with a form asks before it is left with something typed and not saved (journey-leaving.ts). */
export const unsavedChangesGuard: CanDeactivateFn<LeavableScreen> = (screen) => screen.mayLeave();
